import {
  ActionReducerMapBuilder,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { History } from '@vk-hr-tek/core/history';

import {
  DashboardService,
  GetUserWidgetQuickActionsResponse,
  GetUserWidgetQuickActionsParams,
} from '@app/gen/dashboard';
import { ThunkExtra } from '@app/store';
import { DynamicEntity } from '@app/types';

import { DashboardState } from '../../dashboard.state';

export const getQuickActionsSettings = createAsyncThunk<
  {
    company: GetUserWidgetQuickActionsResponse;
    user: { links: GetUserWidgetQuickActionsResponse['links'] };
  },
  GetUserWidgetQuickActionsParams,
  ThunkExtra
>(
  'quickActions/getQuickActionsSettings',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getUserWidgetQuickActions({
        params,
      });

      return { company: result, user: { links: result.links } };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const setQuickActionsSettings = createAsyncThunk<
  void,
  {
    values: { links: GetUserWidgetQuickActionsResponse['links'] };
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra
>(
  'quickActions/setQuickActionsSettings',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const history = inject<History>(History);
      const service = inject(DashboardService);

      await service.updateUserWidgetQuickActions({
        body: {
          links: values.links || [],
        },
      });

      actions.resolve(null);
      history.push('/');
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно сохранён!',
            message: 'Быстрые действия',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      const history = inject<History>(History);

      actions.reject({ [FORM_ERROR]: err });
      history.push('/');
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Быстрые действия',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const quickActionsSettingsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(getQuickActionsSettings.pending, (state) => {
    state.quickActionsSettings.status = 'loading';
    state.quickActionsSettings.companyLinks = [];
    state.quickActionsSettings.userLinks = [];
    state.quickActionsSettings.error = null;
  });
  builder.addCase(getQuickActionsSettings.fulfilled, (state, { payload }) => {
    state.quickActionsSettings.status = 'complete';
    state.quickActionsSettings.companyLinks = payload.company.actions || [];
    state.quickActionsSettings.userLinks = payload.user.links || [];
    state.quickActionsSettings.error = null;
  });
  builder.addCase(
    getQuickActionsSettings.rejected,
    (state, { payload, error }) => {
      state.quickActionsSettings.status = 'failed';
      state.quickActionsSettings.companyLinks = [];
      state.quickActionsSettings.userLinks = [];
      state.quickActionsSettings.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
